import {
  Box,
  type BoxProps,
  Card,
  EditableTitleField,
  Icon,
  type IconName,
  IconTile,
  Text,
} from "@salestrak/ui";
import type { ReactNode } from "react";

import {
  FORM_BANNER_TILE_HEIGHT,
  FormBanner,
  hasFormBanner,
} from "#app/components/forms/form-banner";
import { isFieldItem, type Form, UNTITLED_FORM } from "#app/lib/forms";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";

const COVER_SHEET_ROWS = 4;

function FormCoverSheet({ form }: { form: Pick<Form, "items"> }) {
  const rows = form.items.filter(isFieldItem).slice(0, COVER_SHEET_ROWS);
  return (
    <Box
      background="sunkenVeil"
      height={FORM_BANNER_TILE_HEIGHT}
      overflow="clip"
      paddingLeft="lg"
      paddingRight="lg"
      paddingTop="base"
      shrink={false}
    >
      {rows.length === 0 ? (
        <Box align="center" fill="size" justify="center">
          <IconTile color="muted" name="clipboard-check" size="lg" />
        </Box>
      ) : (
        <Box
          border
          direction="column"
          fill="height"
          gap="xs"
          padding="sm"
          rounded="md"
          roundedSide="top"
          surface="card"
        >
          {rows.map((item) => (
            <Box align="center" direction="row" gap="xs" key={item.itemId}>
              <Icon
                color="faint"
                name={item.kind === "attachment" ? "image" : "square"}
                size="sm"
              />
              <Text size="2xs" truncate variant="faint">
                {item.label}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function MetaCount({ icon, label }: { icon: IconName; label: string }) {
  return (
    <Box align="center" direction="row" gap="xs" shrink={false}>
      <Icon color="faint" name={icon} size="sm" />
      <Text size="xs" variant="muted">
        {label}
      </Text>
    </Box>
  );
}

export function FormTile({
  form,
  date,
  meta,
  badge,
  menu,
  onOpen,
  rename,
}: {
  form: Pick<Form, "name" | "description" | "bannerUrl" | "bannerMediaId" | "items">;
  date: string | null;
  meta: ReactNode;
  badge?: ReactNode;
  menu?: ReactNode;
  onOpen: () => void;
  rename?: {
    editing: boolean;
    onCommit: (next: string) => void;
    onEnd: () => void;
  };
}) {
  const title = form.name || UNTITLED_FORM;
  const editing = rename?.editing ?? false;
  return (
    <Card
      aria-label={title}
      direction="column"
      fill="height"
      group="card"
      position="relative"
      radius="lg"
      {...(editing
        ? {}
        : {
            cursor: "pointer" as const,
            hoverBackground: true,
            onClick: onOpen,
            pressFeedback: true,
            role: "link",
          })}
    >
      {hasFormBanner(form) ? (
        <FormBanner alt="" form={form} height={FORM_BANNER_TILE_HEIGHT} />
      ) : (
        <FormCoverSheet form={form} />
      )}
      {badge || menu ? (
        // The popup is portalled but React events bubble the React tree; stop a corner press reaching the card's onClick.
        <Box
          align="center"
          direction="row"
          gap="xs"
          onClick={(event) => event.stopPropagation()}
          position="absolute"
          right={8}
          top={8}
        >
          {badge}
          {menu ? <Box hoverReveal="card">{menu}</Box> : null}
        </Box>
      ) : null}
      <Box direction="column" fill="flex" gap="base" justify="between" padding="md">
        <Box direction="column" gap="2xs" minWidth={0}>
          <Box align="baseline" direction="row" gap="sm" justify="between">
            {rename && editing ? (
              // `flushLeft` folds the field's inset back so renaming moves no glyph.
              <EditableTitleField
                aria-label={`Rename ${title}`}
                editing
                fill
                flushLeft
                onCommit={rename.onCommit}
                onEditingChange={(next) => {
                  if (!next) rename.onEnd();
                }}
                chrome="none"
                placeholder={UNTITLED_FORM}
                size="default"
                value={form.name}
                weight="semibold"
              />
            ) : (
              // A flex row with paddingY keeps the title row one height in both states.
              <Box direction="row" minWidth={0} paddingY="2xs">
                <Text truncate weight="semibold">
                  {title}
                </Text>
              </Box>
            )}
            {date ? (
              <Box shrink={false}>
                <Text size="xs" variant="faint">
                  {formatRelativeDate(date, "compact")}
                </Text>
              </Box>
            ) : null}
          </Box>
          <Text size="sm" truncate={2} variant="muted">
            {form.description?.trim() || "No description"}
          </Text>
        </Box>
        <Box align="center" direction="row" gap="base" wrap>
          {meta}
        </Box>
      </Box>
    </Card>
  );
}

export function FormActionTile({
  label,
  ...props
}: { label: string } & Omit<BoxProps<"button">, "as" | "children">) {
  return (
    <Box
      align="center"
      as="button"
      border
      borderStyle="dashed"
      cursor="pointer"
      direction="column"
      fill="size"
      gap="sm"
      hoverBackground
      justify="center"
      minHeight={200}
      padding="md"
      pressFeedback
      rounded="lg"
      type="button"
      {...props}
    >
      <IconTile color="muted" name="plus" size="lg" />
      <Text size="sm" variant="muted" weight="medium">
        {label}
      </Text>
    </Box>
  );
}
